/**
 * Video Tour Modal Component
 * Embedded video walkthrough for new users
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../App';
import {
  PlayCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  X,
  Video,
  FileText,
  Stethoscope,
  Mic,
  Calendar,
  BarChart3,
  Upload,
  BookOpen,
} from 'lucide-react';

const videoChapters = [
  {
    id: 'intro',
    title: 'Welcome to The HospiceGuard',
    duration: '1:30',
    icon: PlayCircle,
    description: 'Overview of the platform and its key features',
    topics: ['Dashboard overview', 'Navigation basics', 'Quick tips'],
  },
  {
    id: 'visit-audit',
    title: 'Visit Note Auditing',
    duration: '3:00',
    icon: FileText,
    description: 'How to upload and analyze visit notes',
    topics: ['Uploading documents', 'AI analysis', 'Score interpretation', 'Improving notes'],
  },
  {
    id: 'eligibility',
    title: 'Eligibility Assessment',
    duration: '2:30',
    icon: Stethoscope,
    description: 'Determining hospice eligibility with AI',
    topics: ['LCD criteria', 'Document requirements', 'Assessment results'],
  },
  {
    id: 'voice',
    title: 'Voice-to-Documentation',
    duration: '2:00',
    icon: Mic,
    description: 'Hands-free documentation with speech recognition',
    topics: ['Recording notes', 'AI transcription', 'Editing and saving'],
  },
  {
    id: 'batch',
    title: 'Batch Processing',
    duration: '2:00',
    icon: Upload,
    description: 'Processing multiple files at once',
    topics: ['File upload', 'Batch settings', 'Results export'],
  },
  {
    id: 'templates',
    title: 'Templates Library',
    duration: '1:30',
    icon: BookOpen,
    description: 'Using pre-built documentation templates',
    topics: ['Finding templates', 'Copying content', 'Creating custom templates'],
  },
  {
    id: 'calendar',
    title: 'Compliance Calendar',
    duration: '1:30',
    icon: Calendar,
    description: 'Tracking compliance deadlines',
    topics: ['Adding events', 'Overdue alerts', 'F2F tracking'],
  },
  {
    id: 'analytics',
    title: 'Analytics Dashboard',
    duration: '2:00',
    icon: BarChart3,
    description: 'Understanding your compliance metrics',
    topics: ['Compliance trends', 'Clinician performance', 'ROI calculation'],
  },
];

export const VideoTourModal = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [currentChapter, setCurrentChapter] = useState(0);
  const [watchedChapters, setWatchedChapters] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Load watched chapters from localStorage
    if (user) {
      const watched = localStorage.getItem(`video_tour_watched_${user.id}`);
      if (watched) {
        setWatchedChapters(JSON.parse(watched));
      }
    }
  }, [user]);

  const markChapterWatched = (chapterId) => {
    if (!watchedChapters.includes(chapterId)) {
      const updated = [...watchedChapters, chapterId];
      setWatchedChapters(updated);
      if (user) {
        localStorage.setItem(`video_tour_watched_${user.id}`, JSON.stringify(updated));
      }
    }
  };

  const handleChapterSelect = (index) => {
    setCurrentChapter(index);
    setIsPlaying(true);
    markChapterWatched(videoChapters[index].id);
  };

  const handleNextChapter = () => {
    if (currentChapter < videoChapters.length - 1) {
      handleChapterSelect(currentChapter + 1);
    }
  };

  const chapter = videoChapters[currentChapter];
  const progress = (watchedChapters.length / videoChapters.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Video className="w-5 h-5 text-emerald-500" />
                Video Training Guide
              </DialogTitle>
              <DialogDescription>
                Learn how to use The HospiceGuard effectively
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-emerald-600">
                {watchedChapters.length}/{videoChapters.length} completed
              </Badge>
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Video Player Area */}
          <div className="flex-1 flex flex-col p-6">
            {/* Simulated Video Player */}
            <div className="relative bg-gray-900 rounded-xl aspect-video flex items-center justify-center overflow-hidden">
              {isPlaying ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <chapter.icon className="w-16 h-16 mb-4 text-emerald-400" />
                  <h3 className="text-xl font-bold mb-2">{chapter.title}</h3>
                  <p className="text-gray-400 text-center max-w-md px-4">{chapter.description}</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {chapter.topics.map((topic, i) => (
                      <Badge key={i} variant="secondary" className="bg-gray-800">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-8 text-sm text-gray-500">
                    Video content simulated for demo - Real videos can be embedded here
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setIsPlaying(true);
                    markChapterWatched(chapter.id);
                  }}
                  className="flex flex-col items-center gap-4 hover:scale-105 transition-transform"
                >
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <PlayCircle className="w-12 h-12 text-emerald-500" />
                  </div>
                  <span className="text-white font-medium">Click to Play</span>
                </button>
              )}
            </div>

            {/* Chapter info and controls */}
            <div className="mt-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{chapter.title}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {chapter.duration}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={currentChapter === 0}
                  onClick={() => handleChapterSelect(currentChapter - 1)}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNextChapter}
                  disabled={currentChapter === videoChapters.length - 1}
                >
                  Next Chapter
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>

          {/* Chapter List Sidebar */}
          <div className="w-72 border-l bg-gray-50/50 dark:bg-gray-900/50">
            <div className="p-4 border-b">
              <h4 className="font-medium">Chapters</h4>
            </div>
            <ScrollArea className="h-[calc(100%-60px)]">
              <div className="p-2">
                {videoChapters.map((ch, index) => {
                  const Icon = ch.icon;
                  const isWatched = watchedChapters.includes(ch.id);
                  const isCurrent = index === currentChapter;
                  
                  return (
                    <button
                      key={ch.id}
                      onClick={() => handleChapterSelect(index)}
                      className={`w-full text-left p-3 rounded-lg mb-1 transition-colors
                        ${isCurrent ? 'bg-emerald-500/10 border border-emerald-500/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isCurrent ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${isCurrent ? 'text-emerald-600' : ''}`}>
                              {ch.title}
                            </span>
                            {isWatched && (
                              <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{ch.duration}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoTourModal;
